import { pathOr } from 'ramda';

import {
  AGENT_TYPE,
  AgentType,
  GroupMemberListItem,
  SupplierGroupMember,
  type PreferredDateFormat,
  type PreferredUnitSystem,
  type UserProfile,
} from '@frontend/api';

import { PAPERLESS_TERMS_TYPES, ROLES } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { type FeatureFlag } from 'modules/common/drawers/featureFlagDrawer/const/featureFlags';
import type { FeatureFlagFormData } from 'modules/common/drawers/featureFlagDrawer/types';

import type { RootState } from 'store/configureStore';

const getScope = (state: RootState) => state.auth;

export const getToken = (state: RootState) => getScope(state).accessToken;

export const getIsFetching = (state: RootState) => getScope(state).isFetching;

/* User-related selectors */

export const getUser = (state: RootState): UserProfile | null =>
  getScope(state).user;

export const getUserFullName = (state: RootState) => {
  const user = getUser(state);
  const firstName = user?.firstName;
  const lastName = user?.lastName;
  return firstName && lastName ? `${firstName} ${lastName}` : user?.email;
};

export const getUserEmail = (state: RootState) => getUser(state)?.email;

export const getUserFirstName = (state: RootState) => getUser(state)?.firstName;

export const getUserLastName = (state: RootState) => getUser(state)?.lastName;

export const getIsLoggedIn = (state: RootState): boolean =>
  Boolean(getUser(state));

export const getMscSpecialistId = (state: RootState) =>
  getUser(state)?.mscSpecialistId;

export const getIsSuperuser = (state: RootState) =>
  !!getUser(state)?.isSuperuser;

export const getIsTwoFactorAuthEnabled = (state: RootState) =>
  getUser(state)?.isTwoFactorAuthEnabled;

export const getGroupMemberUserId = (state: RootState) =>
  pathOr(null, ['groupMember', 'userId'], getUser(state));

export const getUserId = (state: RootState) =>
  pathOr(null, ['id'], getUser(state));

export const getAvailableAgents = (state: RootState): AgentType[] =>
  pathOr([], ['availableAgents'], getUser(state)) ?? [];

export const getIsQuoteAgentEnabled = (state: RootState) =>
  getAvailableAgents(state).includes(AGENT_TYPE.QUOTE);

export const getGroupMemberSupplierRoles = (state: RootState) =>
  getUser(state)?.groupMember?.supplierRoles ??
  ({} as SupplierGroupMember['supplierRoles']);

export const isUserGroupAdmin = (state: RootState) =>
  pathOr(false, ['groupMember', 'isAdmin'], getUser(state));

export const isSuperUserOrGroupAdmin = (state: RootState) => {
  return getUser(state)?.isSuperuser || isUserGroupAdmin(state);
};

export const getGroupMemberId = (state: RootState) =>
  pathOr(null, ['groupMember', 'id'], getUser(state));

export const getAvailableGroupMembers = (
  state: RootState,
): GroupMemberListItem[] => getUser(state)?.availableGroupMembers ?? [];

/* UserGroup-related selectors */

export const getActiveGroup = (state: RootState) => getUser(state)?.group;

export const getAvailableGroups = (state: RootState) =>
  getUser(state)?.groups ?? [];

export const getIsPurchaseComponentRealTimePricingEnabled = (
  state: RootState,
) => (getActiveGroup(state) || {}).isPurchaseComponentRealTimePricingEnabled;

export const getLeadSourceSignUpFlow = (state: RootState) =>
  getActiveGroup(state)?.leadSourceSignUpFlow;

export const getLeadSource = (state: RootState) =>
  getActiveGroup(state)?.leadSource;

export const getLeadTimeUnit = (state: RootState) =>
  (getActiveGroup(state) || {}).leadTimeUnit;

export const getStandardLeadTime = (state: RootState) =>
  (getActiveGroup(state) || {}).standardLeadTimeTemplate;

export const getDefaultInternalQuoteNotes = (state: RootState) =>
  (getActiveGroup(state) || {}).defaultInternalQuoteNotes;

export const getGroupId = (state: RootState) =>
  (getActiveGroup(state) || {}).locator;

export const getGroupPrimaryKey = (state: RootState) =>
  (getActiveGroup(state) || {}).id;

export const getSupplierTaxRate = (state: RootState) =>
  (getActiveGroup(state) || {}).taxRate;

export const isSupplier = (state: RootState) =>
  (getActiveGroup(state) || {}).role === ROLES.SUPPLIER;

export const isBuyer = (state: RootState) =>
  (getActiveGroup(state) || {}).role === ROLES.BUYER;

export const getPaymentMethod = (state: RootState) =>
  (getActiveGroup(state) || {}).paymentInformation;

export const getBankingInfo = (state: RootState) =>
  (getActiveGroup(state) || {}).bankingInformation;

export const getBusinessName = (state: RootState) =>
  (getActiveGroup(state) || {}).businessName;

export const getDefaultCreditCardsEnabledForAccounts = (state: RootState) =>
  (getActiveGroup(state) || {}).defaultCreditCardsEnabledForAccounts;

export const getDefaultFacilityCountry = (state: RootState) =>
  (getActiveGroup(state) || {}).defaultFacilityCountry ?? {
    abbr: 'USA',
    alpha2: 'US',
  };

export const getDefaultPaymentTermsForAccounts = (state: RootState) =>
  (getActiveGroup(state) || {}).defaultPaymentTermsForAccounts;

export const getDefaultPurchaseOrdersEnabledForAccounts = (state: RootState) =>
  (getActiveGroup(state) || {}).defaultPurchaseOrdersEnabledForAccounts;

export const getTaskDueDateDeltaHours = (state: RootState) =>
  (getActiveGroup(state) || {}).taskDueDateDeltaHours;

export const getHasCustomerSupportRecord = (state: RootState) =>
  (getActiveGroup(state) ?? {}).hasCustomerSupportRecord;

export const hasCreditCardCheckoutEnabled = (state: RootState) =>
  (getActiveGroup(state) || {}).creditCardCheckout;

export const hasStripeAccount = (state: RootState) =>
  (getActiveGroup(state) || {}).hasStripeAccount;

export const getIsEnforceMfaOrSsoForShareRecipientsEnabled = (
  state: RootState,
) => getActiveGroup(state)?.isEnforceMfaOrSsoForShareRecipientsEnabled;

export const isStripeAccountCompliant = (state: RootState) => {
  return (getActiveGroup(state) || {}).isStripeAccountCompliant;
};

export const getStripeAccountNeedsAdditionalInfo = (state: RootState) => {
  return (getActiveGroup(state) || {}).stripeAccountNeedsAdditionalInfo;
};

export const getIsDiscountsEnabled = (state: RootState) =>
  getActiveGroup(state)?.isDiscountsEnabled;

export const getPreferredDateFormat = (state: RootState): PreferredDateFormat =>
  getActiveGroup(state)?.preferredDateFormat;

export const getPreferredUnitSystem = (
  state: RootState,
): PreferredUnitSystem | undefined =>
  getActiveGroup(state)?.preferredUnitSystem;

export const getFeatureFlags = (state: RootState) =>
  (getActiveGroup(state)?.featureFlags ?? {}) as FeatureFlagFormData;

export const getUserGroupCollectionId = (state: RootState): number =>
  getActiveGroup(state)?.userGroupCollectionId;

/**
 * @deprecated For use only with legacy components wrapped with `connect`, or in sagas. In all other cases, use `useFeatureFlagValue` instead.
 */
export const getFeatureFlagValue = <K extends FeatureFlag>(
  state: RootState,
  featureFlagKey: K,
): FeatureFlagFormData[K] => getFeatureFlags(state)[featureFlagKey];

export const getIsSupplierDdtcRegistered = (state: RootState) =>
  isSupplier(state) && getFeatureFlags(state)[featureFlagKeys.ddtcRegistered];

export const getSubscriptions = (state: RootState) => {
  const subscriptions = (getActiveGroup(state) || {}).subscriptions;
  if (subscriptions)
    return subscriptions.sort(
      (a, b) => parseFloat(a.price) - parseFloat(b.price),
    );
  return subscriptions || [];
};

/* Paperless Parts Terms and Conditions */

export const getFabricatorTerms = (state: RootState) =>
  (getScope(state).paperlessTerms || []).find(
    (terms) => terms.termsType === PAPERLESS_TERMS_TYPES.FABRICATOR_TERMS,
  );

export const getPrivacyPolicy = (state: RootState) =>
  (getScope(state).paperlessTerms || []).find(
    (terms) => terms.termsType === PAPERLESS_TERMS_TYPES.PRIVACY_POLICY,
  );
